import React from "react";
//#region types
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { StaticInfoMessage } from "components/common";
import { Grid } from "@mui/material";
//#endregion
import { useDebounce } from "use-debounce";
import { LocalizationHelperInstance } from "utils/helpers";

export interface ReferenceCodeChangeWarningProps {
  /** The code the person had when the dialog opened. No initial code means nothing can be lost. */
  initialReferenceId?: string;
  referenceId?: string;
  name?: string;
}

// The field validates on every keystroke, so reacting to the raw value would pop the message in and
// push the rest of the dialog down while a replacement code is still half-typed. An emptied field has no
// half-typed state, so it skips the delay — otherwise clearing and saving within the window goes unwarned.
const WARNING_DEBOUNCE_MS = 600;

/**
 * Warns that saving replaces — or removes — a reference code the person already had. Clearing the field
 * is the most destructive edit of the two, so it warns as well: the old code applies to every future
 * invoice to that person.
 */
const ReferenceCodeChangeWarning: React.FC<ReferenceCodeChangeWarningProps> = ({
  initialReferenceId,
  referenceId,
  name,
}) => {
  const [debouncedReferenceId] = useDebounce(referenceId, WARNING_DEBOUNCE_MS);
  // While typing into an emptied field the debounced value still lags at "", which would flash the remove
  // warning; treat that as "not decided yet" instead.
  const shownReferenceId = referenceId ? debouncedReferenceId || initialReferenceId : referenceId;

  if (!initialReferenceId || shownReferenceId === initialReferenceId) return <></>;

  return (
    <Grid item sx={{ marginTop: "16px" }}>
      <StaticInfoMessage displayType={StyleEnums.DisplayType.Warning}>
        {LocalizationHelperInstance.getFormattedMessage(
          shownReferenceId
            ? TranslationKeys.Invoice.ReferenceCodeOverwriteWarningText
            : TranslationKeys.Invoice.ReferenceCodeRemoveWarningText,
          "",
          { oldCode: initialReferenceId, name: name ?? "" }
        )}
      </StaticInfoMessage>
    </Grid>
  );
};

export default ReferenceCodeChangeWarning;
